import {
  PreloadAllModules,
  type Routes,
  mapToCanActivate,
  provideRouter,
  withPreloading,
  withViewTransitions,
} from '@angular/router'
import {
  addressInfoPage,
  adyenCheckoutPage,
  cartInfoPage,
  catalogPage,
  contactInfoPage,
  dashboardPage,
  deliveryInfoConfirmationPage,
  documentListPage,
  editCheckoutAddressPage,
  galleryPage,
  orderDetailPage,
  orderListPage,
  packDetailPage,
  paymentMethodsPage,
  productDetailPage,
  shareEntityPage,
} from '@cart/src/app/app-routes'
import {AuthGuard} from '@services/guards/auth-guard.service'
import {AuthOrInitGuard} from '@services/guards/auth-or-init.guard'
import {CustomOrganizationThemeGuard} from '@services/guards/custom-organization-theme-guard.service'
import {ForgotPasswordGuard} from '@services/guards/forgot-password.guard'
import {ThemeGuard} from '@services/guards/theme.guard'
import {LoginGuard} from './services/guards/login.guard'
import {NoInternetGuard} from './services/guards/no-internet.guard'

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/home?redirect=true',
    pathMatch: 'full',
  },
  {
    path: 'home',
    loadComponent: () => import('@pages/home/home.page').then(m => m.HomePage),
    canActivate: mapToCanActivate([NoInternetGuard, AuthOrInitGuard, ThemeGuard]),
  },
  {
    path: 'auth',
    loadComponent: () => import('@pages/auth/auth.page').then(m => m.AuthPage),
    canActivate: mapToCanActivate([NoInternetGuard, LoginGuard, ThemeGuard]),
  },
  {
    path: 'no-internet',
    loadComponent: () => import('@pages/no-internet/no-internet.page').then(m => m.NoInternetPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'how-to-delete',
    loadComponent: () => import('@pages/how-to-delete/how-to-delete.page').then(m => m.HowToDeletePage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'auth/redirect/:tmpToken',
    loadComponent: () => import('@pages/auth-redirect/auth-redirect.page').then(m => m.AuthRedirectPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'app-news',
    loadComponent: () => import('@pages/app-news/app-news.page').then(m => m.AppNewsPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'login',
    loadComponent: () => import('@pages/login/login.page').then(m => m.LoginPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'must-change-password/:resetToken',
    loadComponent: () =>
      import('@pages/must-change-password/must-change-password.page').then(m => m.MustChangePasswordPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'login/guest',
    loadComponent: () => import('@pages/login/guest-login/guest-login.page').then(m => m.GuestLoginPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'signup',
    loadComponent: () => import('@pages/signup/signup.page').then(m => m.SignupPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'signup/otp',
    loadComponent: () => import('@pages/signup-otp/signup-otp.page').then(m => m.SignupOtpPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  // {
  // 	path: 'locate',
  // 	loadChildren: () =>
  // 		import('@pages/locate/locate.module').then(m => m.LocateModule),
  // 	canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  // },
  {
    path: 'forgot-password',
    loadComponent: () => import('@pages/forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'settings',
    loadComponent: () => import('@pages/settings/settings.page').then(m => m.SettingsPage),
    canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('@pages/profile-page/user-profile-page.component').then(m => m.UserProfilePageComponent),
    canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  },
  {
    path: 'validate/email/:tmpToken',
    loadComponent: () => import('@pages/validate-email/validate-email.page').then(m => m.ValidateEmailPage),
  },
  {
    path: 'chat',
    loadComponent: () => import('@cart/src/app/pages/chat/chat.page').then(m => m.ChatPage),
    canActivate: mapToCanActivate([CustomOrganizationThemeGuard]),
  },
  {
    path: 'change-password',
    loadComponent: () => import('@pages/change-password/change-password.page').then(m => m.ChangePasswordPage),
    canActivate: mapToCanActivate([ThemeGuard, AuthGuard]),
  },
  {
    path: 'forgot-password-otp',
    loadComponent: () =>
      import('@pages/forgot-password/forgot-password-otp/forgot-password-otp.page').then(m => m.ForgotPasswordOtpPage),
    canActivate: mapToCanActivate([ThemeGuard, ForgotPasswordGuard]),
  },
  {
    path: 'invitation',
    children: [
      {
        path: '',
        loadComponent: () => import('@pages/invitation/invitation.page').then(m => m.InvitationPage),
      },
      {
        path: 'code',
        loadComponent: () =>
          import('@pages/invitation/invitation-code/invitation-code.page').then(m => m.InvitationCodePage),
      },
      {
        path: 'invoice',
        loadComponent: () =>
          import('@pages/invitation/invitation-invoice/invitation-invoice.page').then(m => m.InvitationInvoicePage),
      },
      {
        path: 'contactbook',
        loadComponent: () =>
          import('@pages/invitation/invitation-contactbook/invitation-contactbook.page').then(
            m => m.InvitationContactbookPage
          ),
      },
      {
        path: 'create-client',
        loadComponent: () =>
          import('@pages/invitation/invitation-create-client/invitation-create-client.page').then(
            m => m.InvitationCreateClientPage
          ),
      },
      {
        path: 'search-distributor',
        loadComponent: () =>
          import('@pages/invitation/invitation-search-distributor/invitation-search-distributor.page').then(
            m => m.InvitationSearchDistributorPage
          ),
      },
    ],
    canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  },
  {
    path: 'seller-invitation',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/seller-invitation/seller-invitation.page').then(m => m.SellerInvitationPage),
      },
      {
        path: 'contactbook',
        loadComponent: () =>
          import('@pages/invitation/invitation-contactbook/invitation-contactbook.page').then(
            m => m.InvitationContactbookPage
          ),
      },
    ],
    canActivate: [AuthGuard, ThemeGuard],
  },
  {
    path: 'address-form',
    loadComponent: () => import('@pages/address-form/address-form.page').then(m => m.AddressFormPage),
    canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  },
  {
    path: 'signup-anonymous',
    loadComponent: () => import('@pages/signup-anonymous/signup-anonymous.page').then(m => m.SignupAnonymousPage),
    canActivate: mapToCanActivate([AuthGuard, ThemeGuard]),
  },
  {
    path: 'loading-bind-providers',
    loadComponent: () =>
      import('@pages/loading-bind-providers/loading-bind-providers.page').then(m => m.LoadingBindProvidersPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'terms-and-conditions',
    loadComponent: () =>
      import('@components/terms-and-conditions/terms-and-conditions.component').then(
        m => m.TermsAndConditionsComponent
      ),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: 'store/:organization/:conversationIdEncoded',
    canActivate: mapToCanActivate([CustomOrganizationThemeGuard]),
    children: [
      {path: '', redirectTo: 'timeline', pathMatch: 'full'},
      {
        path: 'timeline',
        children: [
          {
            path: '',
            loadComponent: () => import('@pages/timeline/timeline.page').then(m => m.TimelinePage),
          },
          contactInfoPage,
          dashboardPage,
          orderListPage,
          documentListPage,
          galleryPage,
          orderDetailPage,
          productDetailPage,
          shareEntityPage,
          packDetailPage,
        ],
      },
      {
        path: 'chat',
        children: [
          {
            path: '',
            loadComponent: () => import('@cart/src/app/pages/chat/chat.page').then(m => m.ChatPage),
          },
          contactInfoPage,
          dashboardPage,
          orderListPage,
          documentListPage,
          galleryPage,
          orderDetailPage,
          productDetailPage,
          shareEntityPage,
          packDetailPage,
        ],
      },
      {
        path: 'category',
        children: [
          {path: '', redirectTo: 'catalog', pathMatch: 'full'},
          catalogPage,
          contactInfoPage,
          dashboardPage,
          orderListPage,
          documentListPage,
          galleryPage,
          orderDetailPage,
          productDetailPage,
          shareEntityPage,
          packDetailPage,
        ],
      },
      {
        path: 'cart',
        children: [
          {
            path: '',
            loadComponent: () => import('@pages/cart/cart-page.component').then(m => m.CartPageComponent),
          },
          contactInfoPage,
          dashboardPage,
          orderListPage,
          documentListPage,
          galleryPage,
          shareEntityPage,
          orderDetailPage,
          cartInfoPage,
          deliveryInfoConfirmationPage,
          editCheckoutAddressPage,
          addressInfoPage,
          productDetailPage,
          paymentMethodsPage,
          adyenCheckoutPage,
          packDetailPage,
        ],
      },
      {
        path: 'issues',
        children: [
          {
            path: '',
            loadComponent: () => import('@pages/issues/issues.page').then(m => m.IssuesPage),
          },
          {
            path: 'add-issue',
            loadComponent: () => import('@pages/issues/add-issue/add-issue.page').then(m => m.AddIssuePage),
          },
          contactInfoPage,
          dashboardPage,
          orderListPage,
          documentListPage,
          galleryPage,
          orderDetailPage,
          cartInfoPage,
        ],
      },
      {
        path: 'payment-ok',
        loadComponent: () =>
          import('@components/payment-status/payment-ok/payment-ok.component').then(m => m.PaymentOkComponent),
        canActivate: mapToCanActivate([CustomOrganizationThemeGuard]),
      },
      {
        path: 'payment-ko',
        loadComponent: () =>
          import('@components/payment-status/payment-ko/payment-ko.component').then(m => m.PaymentKoComponent),
        canActivate: mapToCanActivate([CustomOrganizationThemeGuard]),
      },
    ],
  },
  {
    path: 'unknown-error',
    loadComponent: () =>
      import('@components/page-unknown-error/page-unknown-error.component').then(m => m.PageUnknownErrorComponent),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
  {
    path: '**',
    loadComponent: () =>
      import('@components/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
]

/**
 * Branding routes that can be overridden by branded projects.
 * Branded projects import this array and prepend their custom routes.
 *
 * @example
 * // In projects/bomba-bomba/src/app/app.routes.ts
 * import { routes } from '@cart/src/app/app.routes';
 * const brandingRoutes = [{ path: 'privacy-policy', ... }];
 * const allRoutes = [...brandingRoutes, ...routes];
 */
export const brandingRoutes: Routes = [
  {
    path: 'privacy-policy',
    loadComponent: () => import('@pages/privacy-policy/privacy-policy.page').then(m => m.PrivacyPolicyPage),
    canActivate: mapToCanActivate([ThemeGuard]),
  },
]

const allRoutes: Routes = [...brandingRoutes, ...routes]

/**
 * Router provider function.
 *
 * Configures routing with:
 * - PreloadAllModules strategy for faster navigation
 */
export function provideAppRouter() {
  return provideRouter(allRoutes, withPreloading(PreloadAllModules), withViewTransitions())
}
